import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Grid,
  Alert,
  Tooltip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface Package {
  PackageID: number;
  PackageName: string;
  Description: string;
  MonthlyPrice: number;
  SetupFee: number;
  IncludedExtensions: number;
  IncludedDIDs: number;
  IncludedMinutes: number | null;
  ExtraMinuteRate: number;
  Active: boolean;
  LiveOnSite: boolean;
  promo_price: number | null;
  promo_end_date: string | null;
  is_recommended: boolean;
  display_order: number;
  IncludesSMS: boolean;
  sms_included: number;
  sms_rate: number;
  mms_included: number;
  mms_rate: number;
  IncludesRecording: boolean;
  MaxRecordingStorage: number;
  RecStorageRate: number;
}

const SignupManager: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  // Allow string or number for numeric fields during editing
  const [formData, setFormData] = useState<Partial<Package> & {
    MonthlyPrice?: number | string;
    promo_price?: number | string | null;
    SetupFee?: number | string;
    ExtraMinuteRate?: number | string;
    sms_included?: number | string;
    sms_rate?: number | string;
    mms_included?: number | string;
    mms_rate?: number | string;
    MaxRecordingStorage?: number | string;
    RecStorageRate?: number | string;
  }>({});

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/endpoints/packages-management.php');

      if (response.data.success) {
        const packagesData = response.data.data?.packages || [];
        // Sort by display_order
        packagesData.sort((a: Package, b: Package) => (a.display_order || 999) - (b.display_order || 999));
        setPackages(packagesData);
      } else {
        setError('Failed to load packages');
      }
    } catch (err) {
      setError('Failed to load packages');
      console.error('Failed to fetch packages:', err);
    }
    setLoading(false);
  };

  const handleEditPackage = (pkg: Package) => {
    setEditingPackage(pkg);
    setFormData({ ...pkg });
    setEditDialog(true);
  };

  const handleSavePackage = async () => {
    try {
      // Parse numeric fields and map to API field names
      const dataToSend: any = {
        // Map to API expected field names
        name: formData.PackageName,
        description: formData.Description,
        price: parseFloat(formData.MonthlyPrice as any) || 0,
        setup_fee: parseFloat(formData.SetupFee as any) || 0,
        promo_price: formData.promo_price ? parseFloat(formData.promo_price as any) : null,
        promo_end_date: formData.promo_end_date,
        is_recommended: formData.is_recommended ? 1 : 0,
        display_order: formData.display_order,
        live_on_site: formData.LiveOnSite ? 1 : 0,
        is_active: formData.Active ? 1 : 0,
        // Include/SMS/MMS fields
        IncludesSMS: formData.IncludesSMS ? 1 : 0,
        sms_included: parseInt(formData.sms_included as any) || 0,
        sms_rate: parseFloat(formData.sms_rate as any) || 0,
        mms_included: parseInt(formData.mms_included as any) || 0,
        mms_rate: parseFloat(formData.mms_rate as any) || 0,
        // Recording fields
        IncludesRecording: formData.IncludesRecording ? 1 : 0,
        MaxRecordingStorage: parseInt(formData.MaxRecordingStorage as any) || 0,
        RecStorageRate: parseFloat(formData.RecStorageRate as any) || 0
      };

      const response = editingPackage
        ? await api.put('/endpoints/packages-management.php', { id: editingPackage.PackageID, ...dataToSend })
        : await api.post('/endpoints/packages-management.php', dataToSend);

      if (response.data.success) {
        setEditDialog(false);
        fetchPackages();
      } else {
        alert('Failed to save package');
      }
    } catch (err) {
      console.error('Error saving package:', err);
      alert('Error saving package');
    }
  };

  const togglePackageVisibility = async (pkg: Package) => {
    try {
      const response = await api.put('/endpoints/packages-management.php', {
        id: pkg.PackageID,
        live_on_site: pkg.LiveOnSite ? 0 : 1
      });

      if (response.data.success) {
        // Update local state immediately for better UX
        setPackages(packages.map(p =>
          p.PackageID === pkg.PackageID
            ? { ...p, LiveOnSite: !p.LiveOnSite }
            : p
        ));
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Failed to update package visibility');
    }
  };

  const toggleRecommended = async (pkg: Package) => {
    try {
      const response = await api.put('/endpoints/packages-management.php', {
        id: pkg.PackageID,
        is_recommended: pkg.is_recommended ? 0 : 1
      });

      if (response.data.success) {
        setPackages(packages.map(p =>
          p.PackageID === pkg.PackageID
            ? { ...p, is_recommended: !p.is_recommended }
            : p
        ));
      }
    } catch (err) {
      console.error('Error toggling recommended:', err);
    }
  };

  const updateDisplayOrder = async (pkg: Package, direction: 'up' | 'down') => {
    const currentIndex = packages.findIndex(p => p.PackageID === pkg.PackageID);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (swapIndex < 0 || swapIndex >= packages.length) return;

    const currentOrder = pkg.display_order || 999;
    const swapPackage = packages[swapIndex];
    const swapOrder = swapPackage.display_order || 999;

    try {
      // Update both packages' display orders
      await Promise.all([
        api.put('/endpoints/packages-management.php', {
          id: pkg.PackageID,
          display_order: swapOrder
        }),
        api.put('/endpoints/packages-management.php', {
          id: swapPackage.PackageID,
          display_order: currentOrder
        })
      ]);

      fetchPackages();
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  const deletePackage = async (pkg: Package) => {
    if (!window.confirm(`Are you sure you want to delete "${pkg.PackageName}"?`)) {
      return;
    }

    try {
      const response = await api.delete(`/endpoints/packages-management.php?id=${pkg.PackageID}`);
      if (response.data.success) {
        fetchPackages();
      } else {
        alert('Failed to delete package');
      }
    } catch (err) {
      console.error('Error deleting package:', err);
      alert('Error deleting package');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Signup Package Manager
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage packages that appear on the signup page. Toggle visibility, set recommended status, and adjust display order.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Packages</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingPackage(null);
              setFormData({
                Active: true,
                LiveOnSite: false,
                display_order: 999
              });
              setEditDialog(true);
            }}
          >
            Add Package
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="80">Order</TableCell>
                <TableCell>Package Name</TableCell>
                <TableCell>Monthly Price</TableCell>
                <TableCell align="center">Extensions</TableCell>
                <TableCell align="center">Minutes</TableCell>
                <TableCell align="center">Recommended</TableCell>
                <TableCell align="center">Show on Signup</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg, index) => (
                <TableRow key={pkg.PackageID}>
                  <TableCell>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => updateDisplayOrder(pkg, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2">{pkg.display_order || 999}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => updateDisplayOrder(pkg, 'down')}
                        disabled={index === packages.length - 1}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {pkg.PackageName}
                    </Typography>
                    {pkg.Description && (
                      <Typography variant="caption" color="text.secondary">
                        {pkg.Description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {pkg.promo_price ? (
                      <Box>
                        <Typography sx={{ textDecoration: 'line-through' }} variant="body2" color="text.secondary">
                          ${pkg.MonthlyPrice}
                        </Typography>
                        <Typography color="success.main" fontWeight="bold">
                          ${pkg.promo_price}
                        </Typography>
                        {pkg.promo_end_date && (
                          <Typography variant="caption" color="text.secondary">
                            Until {new Date(pkg.promo_end_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography>${pkg.MonthlyPrice}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{pkg.IncludedExtensions}</TableCell>
                  <TableCell align="center">
                    {pkg.IncludedMinutes || 'Unlimited'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => toggleRecommended(pkg)}
                      color={pkg.is_recommended ? 'warning' : 'default'}
                    >
                      {pkg.is_recommended ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={pkg.LiveOnSite}
                      onChange={() => togglePackageVisibility(pkg)}
                      color="primary"
                    />
                    <IconButton size="small" disabled>
                      {pkg.LiveOnSite ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={pkg.Active ? 'Active' : 'Inactive'}
                      size="small"
                      color={pkg.Active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit Package">
                        <IconButton size="small" onClick={() => handleEditPackage(pkg)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Package">
                        <IconButton size="small" color="error" onClick={() => deletePackage(pkg)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">No packages found. Add a package to get started.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Package Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          }
        }}
      >
        <DialogTitle>
          {editingPackage ? 'Edit Package' : 'Create New Package'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Package Name"
                value={formData.PackageName || ''}
                onChange={(e) => setFormData({ ...formData, PackageName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={formData.display_order || 999}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.Description || ''}
                onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Monthly Price"
                value={formData.MonthlyPrice || ''}
                onChange={(e) => setFormData({ ...formData, MonthlyPrice: e.target.value as any })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Promo Price"
                value={formData.promo_price || ''}
                onChange={(e) => setFormData({ ...formData, promo_price: e.target.value as any })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Promo End Date"
                value={formData.promo_end_date || ''}
                onChange={(e) => setFormData({ ...formData, promo_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Setup Fee"
                value={formData.SetupFee || 0}
                onChange={(e) => setFormData({ ...formData, SetupFee: e.target.value as any })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Included Extensions"
                value={formData.IncludedExtensions || 1}
                onChange={(e) => setFormData({ ...formData, IncludedExtensions: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Included DIDs"
                value={formData.IncludedDIDs || 0}
                onChange={(e) => setFormData({ ...formData, IncludedDIDs: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Included Minutes"
                value={formData.IncludedMinutes || ''}
                onChange={(e) => setFormData({ ...formData, IncludedMinutes: e.target.value ? parseInt(e.target.value) : null })}
                helperText="Leave blank for unlimited"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Extra Minute Rate"
                value={formData.ExtraMinuteRate || 0.0125}
                onChange={(e) => setFormData({ ...formData, ExtraMinuteRate: e.target.value as any })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Per minute after included"
              />
            </Grid>

            {/* SMS Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                SMS Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IncludesSMS || false}
                    onChange={(e) => setFormData({ ...formData, IncludesSMS: e.target.checked })}
                  />
                }
                label="Include SMS"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="SMS Included"
                value={formData.sms_included || 0}
                onChange={(e) => setFormData({ ...formData, sms_included: e.target.value as any })}
                disabled={!formData.IncludesSMS}
                helperText="Number included per month"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="SMS Extra Rate"
                value={formData.sms_rate || 0.02}
                onChange={(e) => setFormData({ ...formData, sms_rate: e.target.value as any })}
                disabled={!formData.IncludesSMS}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Per message after included"
              />
            </Grid>

            {/* MMS Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                MMS Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="MMS Included"
                value={formData.mms_included || 0}
                onChange={(e) => setFormData({ ...formData, mms_included: e.target.value as any })}
                helperText="Number included per month"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="MMS Extra Rate"
                value={formData.mms_rate || 0.05}
                onChange={(e) => setFormData({ ...formData, mms_rate: e.target.value as any })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Per message after included"
              />
            </Grid>

            {/* Recording Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Call Recording Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IncludesRecording || false}
                    onChange={(e) => setFormData({ ...formData, IncludesRecording: e.target.checked })}
                  />
                }
                label="Include Recording"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Recording Storage (GB)"
                value={formData.MaxRecordingStorage || 0}
                onChange={(e) => setFormData({ ...formData, MaxRecordingStorage: e.target.value as any })}
                disabled={!formData.IncludesRecording}
                helperText="GB of storage included"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="text"
                label="Extra Storage Rate"
                value={formData.RecStorageRate || 1.00}
                onChange={(e) => setFormData({ ...formData, RecStorageRate: e.target.value as any })}
                disabled={!formData.IncludesRecording}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText="Per GB after included"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Active || false}
                      onChange={(e) => setFormData({ ...formData, Active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.LiveOnSite || false}
                      onChange={(e) => setFormData({ ...formData, LiveOnSite: e.target.checked })}
                    />
                  }
                  label="Show on Signup Page"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_recommended || false}
                      onChange={(e) => setFormData({ ...formData, is_recommended: e.target.checked })}
                    />
                  }
                  label="Mark as Recommended"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePackage}>
            Save Package
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignupManager;